// @ts-nocheck
// ============================================================================
// RtAnalytics Module
// ============================================================================

/**
 * Analytics Module
 * Tracks events from elements with data-rt-event attribute.
 * Also supports viewport visibility tracking with data-rt-view attribute.
 *
 * Supported formats for data-rt-event:
 * - String: "category: action" (e.g., "upsell_product: add_to_cart")
 * - String: "category: action | label: value" (e.g., "product: click | id: 123")
 * - JSON: '{"category": "upsell", "action": "add_to_cart", "label": "product_123"}'
 *
 * Viewport tracking with data-rt-view:
 * - Simple: data-rt-view="rt_view_upsell_carousel"
 * - With options: data-rt-view="rt_view_banner | threshold: 0.8 | once: true"
 *
 * @example
 * // Click tracking
 * <button data-rt-event="upsell_product: add_to_cart">Add to Cart</button>
 * <button data-rt-event="product: view | id: 12345 | name: Ring">View</button>
 * <button data-rt-event='{"category": "cart", "action": "update", "value": 100}'>Update</button>
 *
 * // Viewport tracking - fires when element is visible
 * <div data-rt-view="rt_view_upsell_carousel">Upsell Carousel</div>
 * <div data-rt-view="rt_view_recommendations | threshold: 0.3">Recommendations</div>
 *
 * // Programmatic viewport tracking
 * analytics.trackView('.upsell-carousel', 'rt_view_upsell_carousel');
 */

class RtAnalytics {
  static instance = null;

  /** @type {boolean} */
  _initialized = false;

  /** @type {boolean} */
  _debug = false;

  /** @type {Array<Function>} */
  _subscribers = [];

  /** @type {Array<string>} */
  _trackedEvents = ['click', 'change', 'submit'];

  /** @type {IntersectionObserver | null} */
  _viewObserver = null;

  /** @type {WeakMap<Element, object>} */
  _viewTrackedElements = new WeakMap();

  constructor() {
    if (RtAnalytics.instance) {
      return RtAnalytics.instance;
    }
    RtAnalytics.instance = this;
  }

  /**
   * Initialize the analytics module
   * @param {object} options - Configuration options
   * @param {boolean} [options.debug=false] - Enable debug logging
   * @param {Array<string>} [options.events] - Events to track (default: click, change, submit)
   * @param {boolean} [options.autoTrackViews=true] - Auto-track elements with data-rt-view attribute
   */
  init(options = {}) {
    if (this._initialized) {
      return this;
    }

    this._debug = options.debug || false;
    this._trackedEvents = options.events || this._trackedEvents;

    this._setupEventListeners();

    // Auto-track viewport visibility for elements with data-rt-view
    if (options.autoTrackViews !== false) {
      this.autoTrackViews();
    }

    this._initialized = true;

    this._log('Analytics initialized', { events: this._trackedEvents });

    return this;
  }

  /**
   * Subscribe to analytics events
   * @param {Function} callback - Callback function receiving event data
   * @returns {Function} Unsubscribe function
   */
  subscribe(callback) {
    if (typeof callback !== 'function') {
      console.warn('Analytics subscribe: callback must be a function');
      return () => {};
    }

    this._subscribers.push(callback);

    // Return unsubscribe function
    return () => {
      const index = this._subscribers.indexOf(callback);
      if (index > -1) {
        this._subscribers.splice(index, 1);
      }
    };
  }

  /**
   * Manually track an event
   * @param {object} eventData - Event data object
   * @param {string} eventData.category - Event category
   * @param {string} eventData.action - Event action
   * @param {string} [eventData.label] - Event label
   * @param {number} [eventData.value] - Event value
   * @param {object} [eventData.data] - Additional custom data
   */
  track(eventData) {
    if (!eventData.category || !eventData.action) {
      console.warn('Analytics track: category and action are required');
      return;
    }

    const enrichedData = this._enrichEventData(eventData);
    this._dispatchEvent(enrichedData);
  }

  /**
   * Track when an element becomes visible in the viewport
   * Uses IntersectionObserver for efficient visibility detection
   *
   * @param {string | HTMLElement} elementOrSelector - CSS selector or HTMLElement to observe
   * @param {string} eventName - Event name to track (e.g., 'rt_view_upsell_carousel')
   * @param {object} [options] - Additional options
   * @param {number} [options.threshold=0.5] - Visibility threshold (0-1, default 50% visible)
   * @param {boolean} [options.once=true] - Track only once per element (default true)
   * @param {object} [options.data] - Additional custom data to include with the event
   * @returns {Function} Cleanup function to stop observing
   *
   * @example
   * // Track when upsell carousel is viewed
   * analytics.trackView('.upsell-carousel', 'rt_view_upsell_carousel');
   *
   * // Track with custom threshold and data
   * analytics.trackView('#product-recommendations', 'rt_view_recommendations', {
   *   threshold: 0.3,
   *   data: { section: 'pdp', position: 'below_fold' }
   * });
   *
   * // Track multiple elements
   * analytics.trackView('[data-track-view]', 'rt_view_element');
   */
  trackView(elementOrSelector, eventName, options = {}) {
    const { threshold = 0.5, once = true, data = {} } = options;

    // Initialize the observer if not already created
    if (!this._viewObserver) {
      this._initViewObserver();
    }

    // Get elements to observe
    let elements = [];
    if (typeof elementOrSelector === 'string') {
      elements = Array.from(document.querySelectorAll(elementOrSelector));
    } else if (elementOrSelector instanceof HTMLElement) {
      elements = [elementOrSelector];
    }

    if (elements.length === 0) {
      this._log('trackView: No elements found', { selector: elementOrSelector });
      return () => {};
    }

    // Store tracking config for each element
    elements.forEach((element) => {
      this._viewTrackedElements.set(element, {
        eventName,
        threshold,
        once,
        data,
        tracked: false,
      });
      this._viewObserver.observe(element);
    });

    this._log('trackView: Observing elements', {
      count: elements.length,
      eventName,
      threshold,
    });

    // Return cleanup function
    return () => {
      elements.forEach((element) => {
        this._viewObserver?.unobserve(element);
        this._viewTrackedElements.delete(element);
      });
    };
  }

  /**
   * Initialize the IntersectionObserver for viewport tracking
   * @private
   */
  _initViewObserver() {
    if (typeof IntersectionObserver === 'undefined') {
      console.warn('Analytics: IntersectionObserver not supported');
      return;
    }

    this._viewObserver = new IntersectionObserver(
      (entries) => {
        // eslint-disable-next-line complexity
        entries.forEach((entry) => {
          const config = this._viewTrackedElements.get(entry.target);
          if (!config) {
            return;
          }

          // Check if element meets threshold
          if (entry.isIntersecting && entry.intersectionRatio >= config.threshold) {
            // Skip if already tracked and once is true
            if (config.once && config.tracked) {
              return;
            }

            // Mark as tracked
            config.tracked = true;
            this._viewTrackedElements.set(entry.target, config);

            // Get element identifier for logging
            const elementId =
              entry.target.id ||
              entry.target.dataset.trackId ||
              entry.target.className.split(' ')[0] ||
              entry.target.tagName.toLowerCase();

            // Track the view event
            this.track({
              category: 'viewport',
              action: config.eventName,
              label: elementId,
              data: {
                ...config.data,
                visibility_ratio: Math.round(entry.intersectionRatio * 100),
                element_id: entry.target.id || null,
                element_class: entry.target.className || null,
              },
            });

            // Unobserve if once is true
            if (config.once) {
              this._viewObserver?.unobserve(entry.target);
            }
          }
        });
      },
      {
        // Use multiple thresholds for accurate detection
        threshold: [0, 0.25, 0.5, 0.75, 1.0],
      }
    );
  }

  /**
   * Auto-track elements with data-rt-view attribute
   * Format: data-rt-view="event_name" or data-rt-view="event_name | threshold: 0.3"
   *
   * @example
   * <div data-rt-view="rt_view_upsell_carousel">...</div>
   * <div data-rt-view="rt_view_banner | threshold: 0.8">...</div>
   */
  autoTrackViews() {
    const elements = document.querySelectorAll('[data-rt-view]');

    elements.forEach((element) => {
      const value = element.dataset.rtView;
      if (!value) {
        return;
      }

      // Parse the value: "event_name" or "event_name | threshold: 0.5 | once: false"
      const parts = value.split('|').map((p) => p.trim());
      const eventName = parts[0];
      const options = { data: {} };

      // Parse additional options
      for (let i = 1; i < parts.length; i++) {
        const [key, val] = parts[i].split(':').map((s) => s.trim());
        if (key === 'threshold') {
          options.threshold = parseFloat(val);
        } else if (key === 'once') {
          options.once = val.toLowerCase() !== 'false';
        } else {
          options.data[key] = this._parseValue(val);
        }
      }

      this.trackView(element, eventName, options);
    });

    this._log('autoTrackViews: Initialized', { count: elements.length });
  }

  /**
   * Setup event delegation for tracked events
   * @private
   */
  _setupEventListeners() {
    this._trackedEvents.forEach((eventType) => {
      document.addEventListener(eventType, this._handleEvent.bind(this), { capture: true });
    });
  }

  /**
   * Handle DOM events and extract analytics data
   * @param {Event} event
   * @private
   */
  _handleEvent(event) {
    const element = event.target.closest('[data-rt-event]');
    if (!element) {
      return;
    }

    const eventValue = element.dataset.rtEvent;
    if (!eventValue) {
      return;
    }

    const parsedData = this._parseEventValue(eventValue);
    if (!parsedData) {
      return;
    }

    // Enrich with element and event context
    const eventData = this._enrichEventData({
      ...parsedData,
      eventType: event.type,
      element: {
        tagName: element.tagName.toLowerCase(),
        id: element.id || null,
        classList: Array.from(element.classList),
        text: element.textContent?.trim().substring(0, 100) || null,
      },
    });

    // Extract additional data attributes
    const additionalData = this._extractDataAttributes(element);
    if (Object.keys(additionalData).length > 0) {
      eventData.data = { ...eventData.data, ...additionalData };
    }

    this._dispatchEvent(eventData);
  }

  /**
   * Parse the data-rt-event value
   * Supports multiple formats:
   * - "category: action"
   * - "category: action | label: value | key: value"
   * - JSON object
   *
   * @param {string} value
   * @returns {object | null}
   * @private
   */
  _parseEventValue(value) {
    if (!value || typeof value !== 'string') {
      return null;
    }

    const trimmedValue = value.trim();

    // Try JSON parsing first
    if (trimmedValue.startsWith('{')) {
      try {
        return JSON.parse(trimmedValue);
      } catch (e) {
        this._log('Failed to parse JSON event value', { value, error: e.message });
      }
    }

    // Parse string format: "category: action | key: value | key: value"
    return this._parseStringFormat(trimmedValue);
  }

  /**
   * Parse string format event value
   * @param {string} value
   * @returns {object | null}
   * @private
   */
  _parseStringFormat(value) {
    // Split by pipe for multiple key-value pairs
    const parts = value.split('|').map((part) => part.trim());
    const result = {};

    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      const colonIndex = part.indexOf(':');

      if (colonIndex === -1) {
        this._log('Invalid event format, missing colon', { part });
        continue;
      }

      const key = part.substring(0, colonIndex).trim().toLowerCase().replace(/\s+/g, '_');
      const val = part.substring(colonIndex + 1).trim();

      // First part is always category: action
      if (i === 0) {
        result.category = key;
        result.action = val.toLowerCase().replace(/\s+/g, '_');
      } else {
        // Additional key-value pairs go into data
        if (!result.data) {
          result.data = {};
        }
        // Try to parse numbers and booleans
        result.data[key] = this._parseValue(val);
      }
    }

    if (!result.category || !result.action) {
      this._log('Invalid event format, missing category or action', { value });
      return null;
    }

    return result;
  }

  /**
   * Parse a value string to appropriate type
   * @param {string} value
   * @returns {string | number | boolean}
   * @private
   */
  _parseValue(value) {
    // Boolean
    if (value.toLowerCase() === 'true') {
      return true;
    }
    if (value.toLowerCase() === 'false') {
      return false;
    }

    // Number
    const num = parseFloat(value);
    if (!isNaN(num) && isFinite(num) && String(num) === value) {
      return num;
    }

    return value;
  }

  /**
   * Extract additional data-rt-* attributes from element
   * @param {HTMLElement} element
   * @returns {object}
   * @private
   */
  _extractDataAttributes(element) {
    const data = {};
    const prefix = 'rtEvent';

    // Get all dataset entries that start with rtEvent (but not rtEvent itself)
    for (const [key, value] of Object.entries(element.dataset)) {
      if (key.startsWith(prefix) && key !== 'rtEvent') {
        // Convert rtEventProductId -> product_id
        const cleanKey = key
          .replace(prefix, '')
          .replace(/([A-Z])/g, '_$1')
          .toLowerCase()
          .replace(/^_/, '');

        data[cleanKey] = this._parseValue(value);
      }
    }

    // Also extract common data attributes
    const commonAttrs = ['productId', 'variantId', 'productHandle', 'collectionId'];
    for (const attr of commonAttrs) {
      const dataKey = `rt${attr.charAt(0).toUpperCase()}${attr.slice(1)}`;
      if (element.dataset[dataKey]) {
        const cleanKey = attr.replace(/([A-Z])/g, '_$1').toLowerCase();
        data[cleanKey] = this._parseValue(element.dataset[dataKey]);
      }
    }

    return data;
  }

  /**
   * Enrich event data with timestamp and page context
   * @param {object} eventData
   * @returns {object}
   * @private
   */
  _enrichEventData(eventData) {
    return {
      ...eventData,
      timestamp: Date.now(),
      page: {
        url: window.location.href,
        path: window.location.pathname,
        title: document.title,
        referrer: document.referrer || null,
      },
    };
  }

  /**
   * Dispatch the analytics event
   * @param {object} eventData
   * @private
   */
  _dispatchEvent(eventData) {
    this._log('Tracking event', eventData);

    // Notify subscribers
    this._subscribers.forEach((callback) => {
      try {
        callback(eventData);
      } catch (error) {
        console.error('Analytics subscriber error:', error);
      }
    });

    // Dispatch custom DOM event
    document.dispatchEvent(
      new CustomEvent('rt:analytics', {
        bubbles: true,
        detail: eventData,
      })
    );

    // Integration with common analytics platforms
    this._sendToProviders(eventData);
  }

  /**
   * Send event data to analytics providers
   * @param {object} eventData
   * @private
   */
  _sendToProviders(eventData) {
    this._sendToGoogleAnalytics(eventData);
    this._sendToFacebookPixel(eventData);
    this._sendToShopifyAnalytics(eventData);
    this._sendToKlaviyo(eventData);
  }

  /**
   * Send event to Google Analytics (GTM, GA4, Universal Analytics)
   * @param {object} eventData
   * @private
   */
  _sendToGoogleAnalytics(eventData) {
    const { category, action, label, value, data = {} } = eventData;

    if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push({
        event: action,
        category: category,
        ...(label && { eventLabel: label }),
        ...(value !== undefined && { eventValue: value }),
        ...data,
      });
    } else if (typeof window.gtag === 'function') {
      const eventParams = {
        ...(category && { category: category }),
        ...(label && { event_label: label }),
        ...(value !== undefined && { value: value }),
        ...data,
      };
      window.gtag('event', action, eventParams);
    } else if (typeof window.ga === 'function') {
      window.ga('send', 'event', category, action, label, value);
    }
  }

  /**
   * Send event to Facebook Pixel
   * @param {object} eventData
   * @private
   */
  _sendToFacebookPixel(eventData) {
    const { category, action, label, value, data = {} } = eventData;

    if (typeof window.fbq === 'function') {
      window.fbq('trackCustom', `${category}_${action}`, {
        ...(label && { label }),
        ...(value !== undefined && { value }),
        ...data,
      });
    }
  }

  /**
   * Send event to Shopify Analytics
   * @param {object} eventData
   * @private
   */
  _sendToShopifyAnalytics(eventData) {
    const { category, action, label, value, data = {} } = eventData;

    if (window.ShopifyAnalytics?.lib?.track) {
      window.ShopifyAnalytics.lib.track('Custom Event', {
        category,
        action,
        ...(label && { label }),
        ...(value !== undefined && { value }),
        ...data,
      });
    }
  }

  /**
   * Send event to Klaviyo
   * @param {object} eventData
   * @private
   */
  _sendToKlaviyo(eventData) {
    const { category, action, label, value, data = {} } = eventData;

    if (typeof window._learnq?.push === 'function') {
      window._learnq.push([
        'track',
        `${category} ${action}`,
        {
          ...(label && { label }),
          ...(value !== undefined && { value }),
          ...data,
        },
      ]);
    }
  }

  /**
   * Debug logging
   * @param {string} message
   * @param {any} data
   * @private
   */
  _log(message, data = null) {
    if (!this._debug) {
      return;
    }

    const prefix = '[RtAnalytics]';
    if (data) {
      console.log(prefix, message, data);
    } else {
      console.log(prefix, message);
    }
  }

  /**
   * Enable debug mode
   */
  enableDebug() {
    this._debug = true;
    this._log('Debug mode enabled');
  }

  /**
   * Disable debug mode
   */
  disableDebug() {
    this._debug = false;
  }
}

// Create singleton instance
const analytics = new RtAnalytics();

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => analytics.init());
} else {
  analytics.init();
}

// Export both the class and the singleton instance
export { RtAnalytics };
export default analytics;
